import flattenObject from "../utils/flattenObject";

export default flattenObject({
    errors: {
        errorCreatingPayment:
            "<b>Error while creating payment:</b> please try again later",
        errorSettingAlreadyPaid:
            "<b>Error while saving the already paid payment:</b> please try again later",
        errorDownloadingDocument:
            "<b>Error while downloading the document:</b> please try again later",
        errorGettingDocumentPreview:
            "<b>Error while retrieving the preview of the document:</b> please try again later",
        errorDownloadingReceipt:
            "<b>Error while downloading the receipt:</b> please try again later",
    },
    components: {
        AlreadyPaidModal: {
            title: "Confirm already paid",
            line1: "Do you confirm that you have already paid the full amount?",
            line2: "The creditor will be notified with an email.",
            noteForCreditor: "Note for the creditor:",
            abort: "Abort",
            confirm: "Confirm",
        },
        DocumentsPreviewer: {
            modalTitle: "Documents",
            download: "Download",
            downloadBoundToPayment:
                "You will be able to download the document only after completing the payment.",
            errorLoadingPreview: "An error has occurred.",
            Toolbar: {
                page: "Page:",
                of: "of {totalPages}",
            },
        },
        Header: {
            paymentRequestByCreditor: "Payment request by",
        },
        LanguageSelect: {
            italian: "Italian",
            ita: "ITA",
            english: "English",
            eng: "ENG",
            modal: {
                title: "Select the language",
                confirm: "Confirm",
            },
        },
        PaymentAlreadyExistModal: {
            title: "Confirm operation",
            line1: "A payment session, which involves one or more of the selected payment solutions, has already been started.",
            line2: "Starting this payment session the previous one will be disabled.",
            abort: "Abort",
            proceed: "Proceed",
        },
        PaymentConfirmedSummary: {
            thankYou: "THANK YOU!",
            paymentSucceeded: "PAYMENT SUCCEEDED!",
            paymentConfirmed: "PAYMENT CONFIRMED!",
            summary: "SUMMARY",
            onBehalfOf: "TeamSystem Payments S.r.l. on behalf of {companyName}",
            installment: "Installment {installmentNumber}",
            abbreviatedInstallment: "Inst. {installmentNumber}",
            showInstallments: "Show installments",
            paymentReason: "Payment reason",
            bankTransfer: "Bank account",
            creditOrDebitCard: "Credit/debit card",
            iban: "IBAN ****************{iban}",
            creditCard: "{cardName} **** **** **** {pan}",
            deadline: "Deadline:",
            singleScheduledTooltip: "The payment will be charged on deadline",
            paymentInfo:
                "The charge will be visible in your bank account balance as <i>Teamsystem Pay</i>",
            goBackToPaymentRequest: "Go back to the payment request",
            downloadPaymentReceipt: "Download payment receipt",
            downloadReceipt: "Download receipt",
            modal: {
                title: "Installments payment",
                close: "Close",
                total: "Total",
            },
        },
        PaymentFailedSummary: {
            operationRefused: "Operation refused",
            paymentFailed: "The payment was not successful!",
            summary: "SUMMARY",
            onBehalfOf: "TeamSystem Payments S.r.l. on behalf of {companyName}",
            paymentReason: "Payment reason",
            error: "Error",
            goBackToRequestInfo:
                "Go back to the payment request to try with a new payment.",
            goBackToPaymentRequest: "Go back to the payment request",
        },
        PaymentFailedModal: {
            title: "Payment failed",
            line1: "The payment was unsuccessful.",
            line2: "The payment request is still to be paid. Make a new payment by the due date.",
            error: "Error n. {number}",
            installment: "Installment",
            total: "Total",
            confirm: "I understood",
        },
        PayNowModal: {
            payNowTitle: "Immediate payment",
            payOnDeadlineTitle: "Payment on deadline",
            payNowLine1:
                "You have chosen <b>immediate payment</b> as payment solution.",
            payNowLine2: "The total to pay will be charged at checkout.",
            payOnDeadlineLine1:
                "You have chosen <b>pay on deadline</b> as payment solution.",
            payOnDeadlineLine2:
                "The total to pay will be charged on the day of the deadline.",
            expiredInstallmentsInfo:
                "The expired amounts will be charged to you within 24 hours.",
            confirm: "I understood",
        },
        PaymentSolutionsModal: {
            paySingleInstallmentPlanOnDeadlineTitle: "Pay on deadline",
            payMultipleInstallmentsPlanOnDeadlineTitle: "Pay in installments",
            payNowTitle: "Pay now",
            paySingleInstallmentPlanOnDeadlineLine1:
                "You have chosen the <b>pay on deadline</b> as payment solution.",
            paySingleInstallmentPlanOnDeadlineLine2:
                "The total to pay will be charged on deadline.",
            payMultipleInstallmentsPlanOnDeadlineLine1:
                "You have chosen the <b>payment in installments on deadline</b> as payment solution.",
            payMultipleInstallmentsPlanOnDeadlineLine2:
                "The payments of the installments will be charged on each installment's deadline.",
            payMultipleInstallmentsPlanOnDeadlineLine3:
                "Are you sure you want to proceed?",
            payNowLine1:
                "You have chosen the <b>immediate payment</b> as payment solution.",
            payNowLine2:
                "The total to pay will be charged on the same day you make the payment.",
            expiredInstallmentsInfo:
                "The expired amounts will be charged to you within 24 hours.",
            abort: "Abort",
            confirm: "I understood",
            next: "Next",
        },
        PaymentForm: {
            genericError: "An error has occurred.",
            disabledPaymentRequestError:
                "This request is no longer available because it is disabled.",
            expiredPaymentRequestError:
                "This request is no longer available because it has expired.",
            firstTab: "Documents preview",
            secondTab: "Payment solutions",
            thirdTab: "Payment methods",
            DocumentsPreview: {
                title: "DOCUMENTS PREVIEW",
                sentYouTheDocument: "sent you the following document",
                seeDocuments: "See documents",
                by: "by",
                deadline: "Deadline:",
                totalPaymentAmount: "Payment amount:",
                alreadySettled: "Already settled:",
                totalRemaining: "Total remaining:",
                noAttachedDocuments: "No attached documents.",
                noDocumentsPreviewAvailable:
                    "Documents preview is not available.",
                payTheRequestToSeeDocuments:
                    "The attached documents will be available only after the successful payment of the request.",
                proceedWithPaymentToViewBlockedDocuments:
                    "Some of the attached documents will be available only after the successful payment of the request.",
            },
            PaymentPlans: {
                title: "PAYMENT PLAN",
                payNow: "Pay now",
                payNowInfoTooltip: "The selected amount will be charged today",
                onHoldTooltip: "Payment in progress",
                paymentPlans: {
                    payTotalAmount: "Pay the total amount",
                    payTotalAmountOnDeadline:
                        "Pay the total amount on deadline",
                    payTotalAmountDueDate: "By {dueDate}",
                    payTotalAmountOnDueDate: "Deadline: {dueDate}",
                    payInInstallments: "Pay in installments",
                    payInInstallmentsOnDeadline:
                        "Pay in installments on deadline",
                    payInInstallmentsInfo:
                        "The selected installments will be charged at the time of payment",
                    payInInstallmentsonDeadlineInfo:
                        "Each selected installment will be charged at the installment due date",
                },
                installments: {
                    installment: "Installment - {dueDate} - {amount}",
                },
            },
            PaymentMethods: {
                title: "PAYMENT METHODS",
                noAvailablePaymentMethod:
                    "There are no available payment methods because of the chosen payment plan amount.",
                notAvailableWithOnDeadlinePayment:
                    "Not available with payment on deadline.",
                paymentMethods: {
                    creditDebitCard: "Credit/debit card",
                    payPal: "PayPal",
                    bankDirectDebit: "IBAN (direct debit)",
                    instantBankTransfer: "Instant bank transfer",
                },
            },
        },
        PrivacyCookiePolicyFooter: {
            privacyPolicy: "Privacy Policy",
            cookiePolicy: "Cookie",
        },
        PrivacyCookiePolicyMenu: {
            privacyPolicy: "Privacy Policy",
            cookiePolicy: "Cookie",
        },
        ResizeWrapper: {
            resizeScreenOrChangeOrientation:
                "Unsupported screen size: maximize browser window or change device orientation.",
        },
        Summary: {
            summary: "SUMMARY",
            paidDocumentMessage:
                "<b>The document has already been paid:</b> close the page to exit",
            externalPaidDocumentMessage:
                "<b>Pending payment:</b> waiting for the supplier to confirm in order to validate the payment.",
            scheduledPayDocumentMessage:
                "<b>Scheduled payment</b>: next deadline is {deadline}",
            paymentOnHold:
                "<b>Payment in progress:</b> a payment attempt is in progress for this request.",
            next: "Next",
            proceedToPayment: "Proceed to payment",
            alreadyPaid: "Already paid",
            totalToPay: "Total to pay",
            totalRemaining: "Total remaining",
            details: "Details",
            AttachedDocuments: {
                attachedDocuments: "Attached documents",
                downloadBoundToPayment:
                    "You will be able to download the document only after completing the payment.",
            },
            SummaryBox: {
                deadline: "Deadline:",
                nextInstallmentDeadline: "Next installment deadline:",
                totalPaymentAmount: "Payment amount:",
                alreadySettled: "Already settled:",
                expired: "Expired:",
                totalRemaining: "Total remaining:",
            },
        },
        Tabs: {
            step: "Step {number}",
        },
    },
    views: {
        NotFound: {
            notFoundError: "Page not found.",
        },
        Payment: {
            waitTrasnferToSecurePaymentSite:
                "You are about to be transferred to the secure payment site. Wait a few seconds.",
        },
        ThankYouPage: {
            loading: "Loading...",
            error: "An error has occurred.",
        },
    },
});
